import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, FileText, Copy, Eye, Edit, Trash2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';

const CHANNELS = ['Telegram', 'Email', 'WhatsApp', 'SMS'];

export default function TemplatesPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ name: '', channel: 'Telegram', content: '', variables: [] });
    const [varInput, setVarInput] = useState('');
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchTemplates = useCallback(async () => {
        try {
            const res = await fetch(`${API}/templates`, { headers });
            if (res.ok) setTemplates(await res.json());
        } catch {} finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    useEffect(() => {
        if (searchParams.get('action') === 'novo_template') { closeModal(); setShowModal(true); setSearchParams({}); }
    }, [searchParams]);

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Nome obrigatorio'); return; }
        setSaving(true);
        try {
            const url = editId ? `${API}/templates/${editId}` : `${API}/templates`;
            const method = editId ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
            if (res.ok) { toast.success(editId ? 'Template atualizado' : 'Template criado'); closeModal(); fetchTemplates(); }
            else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
        } catch { toast.error('Erro de conexão'); } finally { setSaving(false); }
    };

    const deleteTemplate = async (id) => {
        if (!window.confirm('Excluir template?')) return;
        const res = await fetch(`${API}/templates/${id}`, { method: 'DELETE', headers });
        if (res.ok) { setTemplates(p => p.filter(t => t.template_id !== id)); toast.success('Excluido'); }
    };

    const openEdit = (t) => {
        setForm({ name: t.name, channel: t.channel, content: t.content || '', variables: t.variables || [] });
        setEditId(t.template_id);
        setShowModal(true);
    };

    const closeModal = () => { setShowModal(false); setEditId(null); setForm({ name: '', channel: 'Telegram', content: '', variables: [] }); setVarInput(''); };

    const addVar = () => {
        if (varInput.trim() && !form.variables.includes(varInput.trim())) {
            setForm({ ...form, variables: [...form.variables, varInput.trim()] });
            setVarInput('');
        }
    };

    return (
        <div className="sv-page" data-testid="templates-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Templates</h2>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>{templates.length} modelos</p>
                </div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={() => { closeModal(); setShowModal(true); }} data-testid="new-template-btn"><Plus size={14} /> Novo Template</button>
            </div>

            {loading ? (
                <div className="sv-card-system sv-empty-state"><Loader2 className="animate-spin" /><p className="sv-empty-state-title">Carregando...</p></div>
            ) : templates.length === 0 ? (
                <div className="sv-card-system sv-empty-state"><FileText /><p className="sv-empty-state-title">Nenhum template</p><p className="sv-empty-state-desc">Crie modelos de mensagem reutilizaveis</p>
                    <button className="sv-btn-gold-system sv-btn-system mt-3" onClick={() => setShowModal(true)}><Plus size={14} /> Criar Template</button></div>
            ) : (
                <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                    {templates.map(tpl => (
                        <div key={tpl.template_id} className="sv-card-system" data-testid={`template-${tpl.template_id}`}>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                                    <FileText size={18} style={{ color: 'var(--sv-gold-primary)' }} />
                                </div>
                                <div className="flex-1 min-w-0"><p className="font-semibold text-sm truncate" style={{ color: 'var(--text)' }}>{tpl.name}</p><span className="sv-badge sv-badge-gold">{tpl.channel}</span></div>
                            </div>
                            {tpl.content && <p className="text-xs mb-2 line-clamp-2" style={{ color: 'var(--muted)' }}>{tpl.content}</p>}
                            <div className="flex flex-wrap gap-1 mb-3">
                                {(tpl.variables || []).map(v => (
                                    <span key={v} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }}>{'{{'}{v}{'}}'}</span>
                                ))}
                            </div>
                            <div className="flex items-center justify-between text-xs" style={{ color: 'var(--muted-2)' }}>
                                <span>{tpl.uses || 0} usos</span>
                                <span>{tpl.created_at ? new Date(tpl.created_at).toLocaleDateString('pt-BR') : ''}</span>
                            </div>
                            <div className="flex gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                                <button className="sv-btn-system flex-1" style={{ padding: '5px' }} onClick={() => openEdit(tpl)}><Edit size={12} /> Editar</button>
                                <button className="sv-btn-system" style={{ padding: '5px 8px', color: '#ef4444' }} onClick={() => deleteTemplate(tpl.template_id)}><Trash2 size={12} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
                    <div className="sv-card-system w-full max-w-lg" data-testid="template-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{editId ? 'Editar Template' : 'Novo Template'}</h3>
                            <button className="sv-btn-system" style={{ padding: 4 }} onClick={closeModal}><X size={14} /></button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Nome</label>
                                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Oferta do Dia"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="template-name-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Canal</label>
                                <div className="flex gap-2">{CHANNELS.map(c => (<button key={c} className={`sv-subnav-chip ${form.channel === c ? 'active' : ''}`} onClick={() => setForm({ ...form, channel: c })}>{c}</button>))}</div>
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Conteudo</label>
                                <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={4} placeholder="Texto do template... Use {{variavel}} para campos dinamicos"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm resize-none" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="template-content-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Variaveis</label>
                                <div className="flex gap-2">
                                    <input type="text" value={varInput} onChange={e => setVarInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addVar()} placeholder="nome_variavel"
                                        className="flex-1 px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} />
                                    <button className="sv-btn-system" onClick={addVar}><Plus size={14} /></button>
                                </div>
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {form.variables.map(v => (
                                        <span key={v} className="text-xs px-2 py-1 rounded-full flex items-center gap-1" style={{ background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }}>
                                            {'{{'}{v}{'}}'}
                                            <button onClick={() => setForm({ ...form, variables: form.variables.filter(x => x !== v) })} className="ml-1"><X size={10} /></button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button className="sv-btn-system" onClick={closeModal}>Cancelar</button>
                            <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="template-save-btn">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : null} {editId ? 'Salvar' : 'Criar Template'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
